<template>
  <div v-if="show" class="fixed inset-0 z-50 overflow-y-auto">
    <div class="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
      <!-- 背景遮罩 -->
      <div 
        v-if="show"
        class="fixed inset-0 transition-opacity"
        @click="closeModal"
      >
        <div class="absolute inset-0 bg-gray-500 opacity-75"></div>
      </div>

      <!-- 模态框内容 -->
      <div class="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full sm:p-6 w-[90vw] max-w-[90vw] mx-auto">
        <!-- 标题 -->
        <div class="flex items-center justify-between mb-4">
          <div>
            <h3 class="text-lg font-medium text-gray-900">
              客户数据导入
            </h3>
            <p class="mt-1 text-sm text-gray-500">
              支持Excel(.xlsx)和CSV格式文件，批量导入客户信息
            </p>
          </div>
          <button
            @click="closeModal"
            class="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XMarkIcon class="h-6 w-6" />
          </button>
        </div>

        <!-- 导入结果显示 -->
        <div v-if="importResult" class="mb-6">
          <div v-if="importResult.success" class="bg-green-50 border border-green-200 rounded-lg p-4">
            <div class="flex items-center">
              <CheckCircleIcon class="h-5 w-5 text-green-600 mr-2" />
              <h4 class="text-sm font-medium text-green-900">导入完成</h4>
            </div>
            <div class="mt-3 grid grid-cols-3 gap-4 text-center text-sm">
              <div>
                <div class="text-lg font-semibold text-green-600">{{ importResult.data.summary.success_count }}</div>
                <div class="text-green-700">成功导入</div>
              </div>
              <div>
                <div class="text-lg font-semibold text-yellow-600">{{ importResult.data.summary.skip_count || importResult.data.summary.duplicate_count || 0 }}</div>
                <div class="text-yellow-700">跳过重复</div>
              </div>
              <div>
                <div class="text-lg font-semibold text-red-600">{{ importResult.data.summary.error_count || 0 }}</div>
                <div class="text-red-700">导入失败</div>
              </div>
            </div>
          </div>

          <div v-else class="bg-red-50 border border-red-200 rounded-lg p-4">
            <div class="flex items-center">
              <ExclamationTriangleIcon class="h-5 w-5 text-red-600 mr-2" />
              <h4 class="text-sm font-medium text-red-900">导入失败</h4>
            </div>
            <div class="mt-2 text-sm text-red-700">
              {{ importResult.message || '导入过程中发生错误' }}
            </div>
          </div>
        </div>

        <!-- 模板下载 -->
        <div class="mb-6">
          <label class="block text-sm font-medium text-gray-700 mb-3">下载导入模板</label>
          <div class="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p class="text-sm text-blue-700 mb-3">
              建议先下载模板文件，按照模板格式准备数据后再进行导入
            </p>
            <p class="text-xs text-blue-600 mb-3">
              💡 提示：单次导入限制为 20,000 条数据，请合理规划导入批次
            </p>
            <div class="flex space-x-3">
            <button 
              @click="downloadTemplate('excel')" 
                :disabled="isDownloadingTemplate"
                class="inline-flex items-center px-3 py-2 text-sm font-medium text-blue-700 bg-white border border-blue-300 rounded-lg hover:bg-blue-50 disabled:opacity-50 transition-colors"
              >
                <DocumentArrowDownIcon class="h-4 w-4 mr-2" />
                Excel模板
            </button>
            <button 
              @click="downloadTemplate('csv')" 
                :disabled="isDownloadingTemplate"
                class="inline-flex items-center px-3 py-2 text-sm font-medium text-blue-700 bg-white border border-blue-300 rounded-lg hover:bg-blue-50 disabled:opacity-50 transition-colors"
              >
                <DocumentArrowDownIcon class="h-4 w-4 mr-2" />
                CSV模板
            </button>
            </div>
          </div>
        </div>

        <!-- 文件上传 -->
        <div class="mb-6">
          <label class="block text-sm font-medium text-gray-700 mb-3">选择导入文件</label>
          
          <div class="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors">
            <DocumentIcon class="mx-auto h-8 w-8 text-gray-400 mb-2" />
            <div class="text-sm text-gray-600">
              <label class="cursor-pointer text-blue-600 hover:text-blue-500 font-medium">
                选择文件
                  <input 
                  type="file" 
                    ref="fileInput"
                  accept=".csv,.xlsx,.xls"
                  @change="handleFileSelect" 
                    class="sr-only" 
                />
                </label>
              或拖拽文件到此处
              </div>
            <p class="text-xs text-gray-500 mt-1">支持CSV, XLSX格式，文件大小限制 10MB</p>
                </div>

          <!-- 选中的文件信息 -->
          <div v-if="selectedFile" class="mt-3 bg-gray-50 border border-gray-200 rounded-lg p-3">
            <div class="flex items-center justify-between">
              <div class="flex items-center">
                <DocumentIcon class="h-4 w-4 text-gray-500 mr-2" />
                <span class="text-sm text-gray-900">{{ selectedFile.name }}</span>
                <span class="text-xs text-gray-500 ml-2">({{ formatFileSize(selectedFile.size) }})</span>
              </div>
              <button @click="removeFile" class="text-gray-400 hover:text-red-500 transition-colors">
                <XMarkIcon class="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>

        <!-- 导入选项 -->
        <div class="mb-6">
          <label class="block text-sm font-medium text-gray-700 mb-3">导入选项</label>
          <div class="space-y-4">
            <!-- 更新选项 -->
            <div class="flex items-center">
              <input 
                type="checkbox" 
                v-model="updateExisting" 
                class="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" 
              />
              <div class="ml-3">
                <label class="text-sm text-gray-700">更新已存在的客户信息</label>
                <p class="text-xs text-gray-500">根据邮箱地址更新已存在客户的信息，否则跳过重复客户</p>
              </div>
            </div>

            <!-- 标签选择 -->
            <div v-if="availableTags.length > 0">
              <div class="flex items-center justify-between mb-2">
                <label class="text-sm text-gray-700">为导入的客户添加标签 (可选)</label>
                <input
                  v-model="tagSearchQuery"
                  type="text"
                  placeholder="搜索标签..."
                  class="w-32 h-7 px-2 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div class="max-h-40 overflow-y-auto pr-2">
                <div v-if="filteredTags.length > 0" class="flex flex-wrap gap-2">
                  <label v-for="tag in filteredTags" :key="tag.id" class="cursor-pointer">
                    <input 
                      type="checkbox" 
                      :value="tag.id"
                      v-model="selectedTagIds"
                      class="sr-only" 
                    />
                    <span 
                      class="inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full transition-colors"
                      :class="selectedTagIds.includes(tag.id) 
                        ? 'bg-blue-100 text-blue-800 border border-blue-300' 
                        : 'bg-gray-100 text-gray-800 border border-gray-300 hover:bg-gray-200'"
                      :style="selectedTagIds.includes(tag.id) 
                        ? { backgroundColor: tag.background_color || '#DBEAFE', color: tag.text_color || '#1E40AF' } 
                        : {}"
                    >
                      {{ tag.name }}
                    </span>
                  </label>
                </div>
                <div v-else class="text-center py-4 text-sm text-gray-400">
                  未找到匹配的标签
                </div>
              </div>
            </div>

            <!-- 分组选择 -->
            <div v-if="availableGroups.length > 0">
              <div class="flex items-center justify-between mb-2">
                <label class="text-sm text-gray-700">为导入的客户添加分组 <span class="text-red-500">*</span></label>
                <input
                  v-model="groupSearchQuery"
                  type="text"
                  placeholder="搜索分组..."
                  class="w-32 h-7 px-2 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div class="max-h-40 overflow-y-auto pr-2">
                <div v-if="filteredGroups.length > 0" class="flex flex-wrap gap-2">
                  <label v-for="group in filteredGroups" :key="group.id" class="cursor-pointer">
                    <input 
                      type="checkbox" 
                      :value="group.id"
                      v-model="selectedGroupIds"
                      class="sr-only" 
                    />
                    <span 
                      class="inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full transition-colors"
                      :class="selectedGroupIds.includes(group.id) 
                        ? 'bg-green-100 text-green-800 border border-green-300' 
                        : 'bg-gray-100 text-gray-800 border border-gray-300 hover:bg-gray-200'"
                    >
                      {{ group.name }}
                      <span v-if="group.description" class="ml-1 text-gray-500">· {{ group.description }}</span>
                    </span>
                  </label>
                </div>
                <div v-else class="text-center py-4 text-sm text-gray-400">
                  未找到匹配的分组
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 操作按钮 -->
        <div class="flex justify-end space-x-3 pt-4 border-t border-gray-200">
          <button
            @click="closeModal"
            type="button"
            class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-colors"
            :disabled="isImporting"
          >
            {{ importResult ? '关闭' : '取消' }}
          </button>
          
          <button 
            v-if="importResult"
            @click="resetImport"
            type="button"
            class="px-4 py-2 text-sm font-medium text-blue-700 bg-blue-50 border border-blue-300 rounded-lg hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-colors"
          >
            重新导入
          </button>
          
          <button 
            v-else
            @click="startImport"
            type="button"
            :disabled="isImporting || !selectedFile"
            class="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center transition-colors"
          >
            <svg v-if="isImporting" class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            {{ isImporting ? '导入中...' : '开始导入' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { useToast } from 'vue-toastification'
import { 
  XMarkIcon, 
  DocumentIcon, 
  DocumentArrowDownIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon
} from '@heroicons/vue/24/outline'
import { customerService } from '@/services/customerService'

// Props
const props = defineProps({
    show: {
      type: Boolean,
      default: false
    },
    tags: {
      type: Array,
      default: () => []
    },
    groups: {
      type: Array,
      default: () => []
  }
})

// Emits
const emit = defineEmits(['close', 'imported'])

// Composables
const toast = useToast()

// State
    const selectedFile = ref(null)
const updateExisting = ref(false)
    const selectedTagIds = ref([])
    const selectedGroupIds = ref([])
const isDownloadingTemplate = ref(false)

// 🔧 搜索状态
const tagSearchQuery = ref('')
const groupSearchQuery = ref('')
const isImporting = ref(false)
const importResult = ref(null)

// File input ref
    const fileInput = ref(null)

// Computed
const availableTags = computed(() => props.tags || [])
const availableGroups = computed(() => props.groups || [])

// 🔧 过滤后的标签和分组
const filteredTags = computed(() => {
  if (!tagSearchQuery.value) {
    return availableTags.value
  }
  const query = tagSearchQuery.value.toLowerCase()
  return availableTags.value.filter(tag => 
    tag.name.toLowerCase().includes(query)
  )
})

const filteredGroups = computed(() => {
  if (!groupSearchQuery.value) {
    return availableGroups.value
  }
  const query = groupSearchQuery.value.toLowerCase()
  return availableGroups.value.filter(group => 
    group.name.toLowerCase().includes(query) || 
    (group.description && group.description.toLowerCase().includes(query))
  )
})

// Watch for modal show/hide
watch(() => props.show, (show) => {
  if (show) {
    resetState()
  }
})

// Methods
const resetState = () => {
  selectedFile.value = null
  updateExisting.value = false
  selectedTagIds.value = []
  selectedGroupIds.value = []
  tagSearchQuery.value = ''  // 🔧 重置搜索
  groupSearchQuery.value = ''  // 🔧 重置搜索
  isImporting.value = false
  importResult.value = null
}

    const handleFileSelect = (event) => {
      const file = event.target.files[0]
      if (file) {
    // 验证文件类型
    const allowedTypes = ['.csv', '.xlsx', '.xls']
    const fileExtension = '.' + file.name.split('.').pop().toLowerCase()
    
    if (!allowedTypes.includes(fileExtension)) {
      toast.error('只支持CSV和Excel文件格式')
      return
    }
    
    // 验证文件大小 (10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('文件大小不能超过10MB')
      return
    }
    
    selectedFile.value = file
  }
}

const removeFile = () => {
  selectedFile.value = null
  if (fileInput.value) {
    fileInput.value.value = ''
  }
}

const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

    const downloadTemplate = async (format) => {
  if (isDownloadingTemplate.value) return
  
  isDownloadingTemplate.value = true
  
      try {
    const result = await customerService.downloadImportTemplate(format)
        
        if (result.success) {
          // 创建下载链接
      const url = URL.createObjectURL(result.blob)
          const a = document.createElement('a')
          a.href = url
          a.download = result.filename
          document.body.appendChild(a)
          a.click()
          document.body.removeChild(a)
      URL.revokeObjectURL(url)
          
        } else {
      toast.error(result.message || '模板下载失败')
        }
      } catch (error) {
        console.error('模板下载失败:', error)
        toast.error('模板下载失败')
      } finally {
    isDownloadingTemplate.value = false
  }
}

const closeModal = () => {
  if (!isImporting.value) {
    emit('close')
  }
}

const startImport = async () => {
  if (!selectedFile.value) {
    toast.error('请选择导入文件')
    return
  }

  // 🔧 验证必须选择分组
  if (selectedGroupIds.value.length === 0 && availableGroups.value.length > 0) {
    toast.error('请至少选择一个分组')
    return
  }

  isImporting.value = true
  importResult.value = null
  
  try {
    const formData = new FormData()
    formData.append('file', selectedFile.value)
    formData.append('update_existing', updateExisting.value)
    
    // 添加标签ID
    if (selectedTagIds.value.length > 0) {
      selectedTagIds.value.forEach(tagId => {
        formData.append('tag_ids', tagId)
      })
    }
    
    // 添加分组ID
    if (selectedGroupIds.value.length > 0) {
      selectedGroupIds.value.forEach(groupId => {
        formData.append('group_ids', groupId)
      })
    }
    
    const result = await customerService.importCustomers(formData)
    console.log('导入结果:', result)
    
    if (result.success) {
      toast.success('客户导入成功!')
      emit('imported', result)
      // 导入成功后自动关闭模态框
      setTimeout(() => {
        emit('close')
      }, 1000)
    } else {
      toast.error(result.message || '导入失败')
      importResult.value = result
    }
  } catch (error) {
    console.error('导入失败:', error)
    importResult.value = {
      success: false,
      message: '导入过程中发生错误，请稍后重试'
    }
    toast.error('导入失败，请稍后重试')
  } finally {
    isImporting.value = false
  }
}

const resetImport = () => {
  importResult.value = null
  resetState()
}
</script>

<style scoped>
/* 自定义样式可以在这里添加 */
</style>
